import Cookies from 'js-cookie'

const SESSION_MARKER_COOKIE = 'pro_session_expiration'
const SESSION_NOTICE_COOKIE = 'pro_session_notice'
const SESSION_NOTICE_HANDLED_COOKIE = 'pro_session_notice_handled'
const SESSION_NOTICE_HANDLED_TTL_DAYS = 5 / 1440
const SESSION_SEEN_STORAGE_KEY = 'pro_session_seen'
const SESSION_NOTICE_TYPES = ['fingerprint_mismatch', 'session_expired', 'session_revoked']
const SHARED_COOKIE_DOMAIN = 'pokemonrevolution.net'
const DEFAULT_LOGIN_URL = 'https://pokemonrevolution.net/en/login'

function loginBaseUrl () {
  return process.env.VUE_APP_PRO_LOGIN_URL || DEFAULT_LOGIN_URL
}

function isBrowser () {
  return typeof window !== 'undefined' && typeof window.location !== 'undefined'
}

export function isLoggedInByCookie () {
  return Boolean(Cookies.get(SESSION_MARKER_COOKIE))
}

function sharedCookieDomain () {
  if (!isBrowser()) {
    return ''
  }

  const hostname = window.location.hostname.toLowerCase()
  if (hostname === SHARED_COOKIE_DOMAIN || hostname.endsWith(`.${SHARED_COOKIE_DOMAIN}`)) {
    return SHARED_COOKIE_DOMAIN
  }
  return ''
}

function removeCookieEverywhere (name) {
  Cookies.remove(name, { path: '/' })

  const domain = sharedCookieDomain()
  if (domain) {
    Cookies.remove(name, { path: '/', domain })
  }
}

function setSharedCookie (name, value, options = {}) {
  Cookies.set(name, value, { path: '/', ...options })

  const domain = sharedCookieDomain()
  if (domain) {
    Cookies.set(name, value, { path: '/', domain, ...options })
  }
}

export function normalizeSessionNotice (reason) {
  return SESSION_NOTICE_TYPES.includes(reason) ? reason : null
}

export function markSessionNoticeHandled () {
  setSharedCookie(SESSION_NOTICE_HANDLED_COOKIE, '1', { expires: SESSION_NOTICE_HANDLED_TTL_DAYS })
}

export function clearSessionNoticeHandled () {
  removeCookieEverywhere(SESSION_NOTICE_HANDLED_COOKIE)
}

export function hasSessionNoticeHandled () {
  return Cookies.get(SESSION_NOTICE_HANDLED_COOKIE) === '1'
}

export function consumeSessionNoticeCookie () {
  const reason = normalizeSessionNotice(Cookies.get(SESSION_NOTICE_COOKIE))
  if (reason) {
    removeCookieEverywhere(SESSION_NOTICE_COOKIE)
    markSessionNoticeHandled()
  }
  return reason
}

export function rememberSessionSeen () {
  if (!isBrowser()) {
    return
  }

  try {
    window.localStorage.setItem(SESSION_SEEN_STORAGE_KEY, '1')
  } catch (e) {}
}

export function forgetSessionSeen () {
  if (!isBrowser()) {
    return
  }

  try {
    window.localStorage.removeItem(SESSION_SEEN_STORAGE_KEY)
  } catch (e) {}
}

export function hasSessionSeen () {
  if (!isBrowser()) {
    return false
  }

  try {
    return window.localStorage.getItem(SESSION_SEEN_STORAGE_KEY) === '1'
  } catch (e) {
    return false
  }
}

export function sessionLossNoticeReason () {
  const explicitReason = consumeSessionNoticeCookie()
  if (explicitReason) {
    return explicitReason
  }

  if (hasSessionNoticeHandled()) {
    forgetSessionSeen()
    return null
  }

  return hasSessionSeen() ? 'session_expired' : null
}

export function sanitizedReturnPath (route) {
  const fallback = '/dashboard'
  const redirect = route && route.query ? route.query.redirect : null
  const candidate = typeof redirect === 'string' && redirect ? redirect : route && route.fullPath

  if (!candidate || candidate === '/auth/login') {
    return fallback
  }

  if (candidate.charAt(0) === '/' && candidate.charAt(1) !== '/') {
    return candidate
  }

  if (isBrowser()) {
    try {
      const parsed = new URL(candidate, window.location.origin)
      if (parsed.origin === window.location.origin) {
        return parsed.pathname + parsed.search + parsed.hash
      }
    } catch (e) {
      return fallback
    }
  }

  return fallback
}

export function dashboardReturnUrl (route) {
  const path = sanitizedReturnPath(route)
  if (!isBrowser()) {
    return path
  }

  return new URL(path, window.location.origin).toString()
}

export function buildLoginUrl (route) {
  const returnTo = dashboardReturnUrl(route)
  const loginUrl = loginBaseUrl()

  try {
    const parsed = new URL(loginUrl)
    parsed.searchParams.set('return_to', returnTo)
    return parsed.toString()
  } catch (e) {
    const separator = loginUrl.indexOf('?') === -1 ? '?' : '&'
    return `${loginUrl}${separator}return_to=${encodeURIComponent(returnTo)}`
  }
}

export function redirectToLogin (route, replace = false) {
  if (!isBrowser()) {
    return
  }

  const target = buildLoginUrl(route)
  if (replace) {
    window.location.replace(target)
  } else {
    window.location.assign(target)
  }
}
